import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOMeta from './SEOMeta';
import { Button } from './ui/button';
import PublicNavbar from './PublicNavbar';
import { getAllPosts, getCategories, getTags } from '../content/blog/queries';
import {
  ArrowRight,
  Calendar,
  Clock,
  Tag,
  Search,
  ChevronRight
} from 'lucide-react';

const BlogList = () => {
  const navigate = useNavigate();
  // Registry-backed, so the list is available on first render — no fetch, no
  // loading state, and the prerendered HTML contains the full post list.
  const posts = getAllPosts();
  const categories = ['All', ...getCategories()];
  const tags = getTags();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTag, setSelectedTag] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const featuredPosts = posts.filter(p => p.featured);

  const filteredPosts = posts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesTag = !selectedTag || post.tags.includes(selectedTag);
    const matchesSearch = !searchQuery ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesCategory && matchesTag && matchesSearch;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedCategory('All');
    setSelectedTag('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEOMeta
        title="Blog: AI Property Management Insights & Landlord Guides"
        description="Practical guides on agentic AI, reducing maintenance costs, tenant communication, and property management best practices for Central Ohio landlords."
        canonical="/blog"
      />
      <PublicNavbar activeRoute="/blog" />

      {/* Hero */}
      <section className="container mx-auto px-4 py-12 md:py-16">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            NivasaPro{' '}
            <span className="bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">
              Blog
            </span>
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Insights on AI-powered property management, Central Ohio real estate,
            and tips for modern landlords.
          </p>
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <section className="container mx-auto px-4 pb-12">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Featured Articles</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {featuredPosts.slice(0, 2).map((post) => (
                <div
                  key={post.slug}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                  onClick={() => navigate(`/blog/${post.slug}`)}
                >
                  <div className="bg-gradient-to-br from-cyan-500 to-blue-600 h-48 flex items-center justify-center">
                    <span className="text-6xl text-white/20 font-bold">NP</span>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-3 mb-3">
                      <span className="bg-cyan-100 text-cyan-800 text-xs font-semibold px-3 py-1 rounded-full">
                        {post.category}
                      </span>
                      <span className="text-gray-500 text-sm flex items-center">
                        <Clock className="h-4 w-4 mr-1" />
                        {post.readTime}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-cyan-600 transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 mb-4 line-clamp-2">{post.excerpt}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-500 flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(post.date)}
                      </span>
                      <span className="text-cyan-600 font-medium flex items-center group-hover:translate-x-1 transition-transform">
                        Read more <ChevronRight className="h-4 w-4 ml-1" />
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Search, Category, and Tag Filters */}
      <section className="container mx-auto px-4 pb-8">
        <div className="max-w-6xl mx-auto space-y-4">
          {/* Search + category row */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg w-full md:w-80 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => { setSelectedCategory(category); setSelectedTag(''); }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-cyan-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* Tag row */}
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-2 items-center">
              <span className="text-xs text-gray-500 font-medium uppercase tracking-wide mr-1">Tags:</span>
              {tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(selectedTag === tag ? '' : tag)}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    selectedTag === tag
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <Tag className="h-3 w-3" />
                  {tag}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Post List */}
      <section className="container mx-auto px-4 pb-16">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {selectedCategory !== 'All' ? selectedCategory : selectedTag ? `#${selectedTag}` : 'All Articles'}
            <span className="text-gray-500 font-normal text-lg ml-2">
              ({filteredPosts.length})
            </span>
          </h2>

          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No articles found matching your criteria.</p>
              <button onClick={clearFilters} className="text-cyan-600 hover:underline mt-2">
                Clear filters
              </button>
            </div>
          ) : (
            <div className="space-y-6">
              {filteredPosts.map((post) => (
                <article
                  key={post.slug}
                  className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg transition-shadow cursor-pointer group"
                  onClick={() => navigate(`/blog/${post.slug}`)}
                >
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className="bg-gray-100 text-gray-700 text-xs font-semibold px-3 py-1 rounded-full">
                          {post.category}
                        </span>
                        <span className="text-gray-500 text-sm flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {post.readTime}
                        </span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-cyan-600 transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 mb-4">{post.excerpt}</p>
                      <div className="flex flex-wrap items-center gap-4">
                        <span className="text-sm text-gray-500 flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {formatDate(post.date)}
                        </span>
                        <div className="flex flex-wrap gap-2">
                          {post.tags.slice(0, 3).map((tag) => (
                            <button
                              key={tag}
                              onClick={(e) => { e.stopPropagation(); setSelectedTag(tag); setSelectedCategory('All'); }}
                              className="text-xs text-gray-500 flex items-center hover:text-cyan-600 transition-colors"
                            >
                              <Tag className="h-3 w-3 mr-1" />
                              {tag}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center text-cyan-600 font-medium group-hover:translate-x-1 transition-transform">
                      Read <ArrowRight className="h-4 w-4 ml-1" />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-cyan-600 to-blue-700">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ready to Transform Your Property Management?
          </h2>
          <p className="text-xl text-cyan-100 mb-8 max-w-2xl mx-auto">
            Put these insights into action with AI-powered automation.
          </p>
          <Button
            size="lg"
            className="bg-white text-cyan-700 hover:bg-gray-100 px-8 py-6 text-lg font-semibold"
            onClick={() => navigate('/')}
          >
            Get Early Access <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-6 w-6" />
                <span className="text-xl font-bold text-white">NivasaPro.ai</span>
              </div>
              <p className="text-sm text-gray-400">
                Agentic rental property management powered by AI.
              </p>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Product</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/features')} className="hover:text-cyan-400 transition text-left">Features</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Pricing</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Security</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Company</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/about')} className="hover:text-cyan-400 transition text-left">About</button></li>
                <li><button onClick={() => navigate('/blog')} className="hover:text-cyan-400 transition text-left">Blog</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Careers</button></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold text-white mb-4">Legal</h3>
              <ul className="space-y-2 text-sm">
                <li><button onClick={() => navigate('/privacy')} className="hover:text-cyan-400 transition text-left">Privacy Policy</button></li>
                <li><button onClick={() => navigate('/terms')} className="hover:text-cyan-400 transition text-left">Terms of Service</button></li>
                <li><button onClick={() => {}} className="hover:text-cyan-400 transition text-left">Contact</button></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2026 NivasaPro.ai. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogList;
